import { z } from 'zod';
import { FLEET_JOB_MESSAGE_MAX_LENGTH } from './fleet-commands.schemas';
import { FLEET_ACCOUNT_LIMITS, FleetAccountSchema } from './fleet-status.schemas';
import {
  FleetAnswerToRelaySchema,
  FleetJobKeyHistorySchema,
  FleetJobQuestionInputSchema,
} from './fleet-questions.schemas';
import { FleetTicketMoveSchema, FleetTicketParkSchema, FleetTicketWriteSchema } from './fleet-next-job.schemas';

/**
 * How a job ended (IMM-914.1, ADR 0047). `released` is the one member a worker
 * may never write — an operator's release is what distinguishes work handed
 * back from work a worker finished or abandoned itself.
 *
 * `no-run` (OPS-31, ADR 0058): the round never reached the model — the host was
 * broken, not the ticket. It did nothing to the ticket and does NOT count toward
 * `FLEET_JOB_MAX_RUNS`; every other end, `abandoned` included, does.
 */
/**
 * The most rounds one work item (`IMM-815-engineer`) may ever be claimed for.
 * A ticket that has not landed in this many rounds is looping — blocked, fixed,
 * blocked again — and every further round is a paid model run that changes
 * nothing. Past it, the claim is refused (`fleet.job.run-limit`) and the ticket
 * is a person's to finish. An operator may raise one key's cap with a grant
 * (`fleet-agent agent jobs top-up`, OPS-33); nothing else resets it.
 */
export const FLEET_JOB_MAX_RUNS = 10;

export const FLEET_JOB_END_REASONS = ['finished', 'parked', 'abandoned', 'no-run', 'released'] as const;
export const FleetJobEndReasonSchema = z.enum(FLEET_JOB_END_REASONS);
export type FleetJobEndReason = (typeof FLEET_JOB_END_REASONS)[number];

/** What a worker may write when it ends its own job — `released` is the operator's. */
export const WorkerFleetJobEndReasonSchema = z.enum(['finished', 'parked', 'abandoned', 'no-run']);
export type WorkerFleetJobEndReason = z.infer<typeof WorkerFleetJobEndReasonSchema>;

export const ClaimFleetJobBodySchema = z.object({
  /** The work item, e.g. `IMM-815-engineer` — the ticket and the lane serving it, never the branch name. */
  key: z.string().min(1).max(255),
  worker: z.string().min(1).max(128),
  metadata: z.record(z.string(), z.unknown()).optional(),
  /** The attended drain (`--label needs-human`) deliberately taking blocked work. */
  allowBlocked: z.boolean().optional(),
  /** The host's accounts as its lane read them just before claiming; absent from an agent that predates it. */
  accounts: z.array(FleetAccountSchema).max(FLEET_ACCOUNT_LIMITS.accounts).optional(),
});

export type ClaimFleetJobBody = z.infer<typeof ClaimFleetJobBodySchema>;

export const ClaimFleetJobResponseSchema = z.object({
  jobId: z.uuid(),
  claimEpoch: z.number().int().positive(),
  claimedAt: z.iso.datetime(),
});

export type ClaimFleetJobResponse = z.infer<typeof ClaimFleetJobResponseSchema>;

export const StartFleetJobBodySchema = z.object({ claimEpoch: z.number().int().positive() });

export type StartFleetJobBody = z.infer<typeof StartFleetJobBodySchema>;

export const StartFleetJobResponseSchema = z.object({ startedAt: z.iso.datetime() });

export type StartFleetJobResponse = z.infer<typeof StartFleetJobResponseSchema>;

export const EndFleetJobBodySchema = z.object({
  claimEpoch: z.number().int().positive(),
  endReason: WorkerFleetJobEndReasonSchema,
  detail: z.string().max(500).optional(),
  /**
   * The question a `parked` end is waiting on (IMM-981) — what the fleet page
   * shows an operator to answer. Refused on any other end reason.
   */
  question: FleetJobQuestionInputSchema.optional(),
  /**
   * Deprecated and ignored: every end now binds its key to the worker that held
   * it, until a manual release. Still accepted so a lane that has not been
   * updated yet keeps working.
   */
  bindWorker: z.boolean().optional(),
  /**
   * ADR 0011: the lane's `move-to` target for a `finished` end, moved by the backend only from a state in `from`.
   * Refused on any other end reason, and with `park`.
   */
  move: FleetTicketMoveSchema.optional(),
  /** ADR 0011: a `parked` end's label, and the ticket back to where the job picked it. Refused on any other. */
  park: FleetTicketParkSchema.optional(),
});

export type EndFleetJobBody = z.infer<typeof EndFleetJobBodySchema>;

export const EndFleetJobResponseSchema = z.object({
  endedAt: z.iso.datetime(),
  /**
   * What the backend did to the ticket: the `move` or `park` the end asked for, or — on any end that is not
   * `finished` — the put-back to where the job picked it (ADR 0011 step 4b). Absent when nothing was asked and the
   * claim never moved the ticket.
   */
  ticket: FleetTicketWriteSchema.optional(),
});

export type EndFleetJobResponse = z.infer<typeof EndFleetJobResponseSchema>;

export const FleetJobPathParamsSchema = z.object({ jobId: z.uuid() });

/**
 * One live job, as a MACHINE reads it (IMM-938).
 *
 * Distinct from `FleetJobView`, which is the operator's read: this one carries
 * the `claimEpoch` that ending a job requires, and omits the silence
 * derivation an operator's list needs.
 *
 * The fleet is a single owner's set of approved machines, so any of them may
 * read the whole list and release any row on it — a host whose own lane is
 * wedged is frequently not the host with a terminal in front of it.
 */
/**
 * The job heartbeat's two windows. Past `FLEET_JOB_STALE_AFTER_MS` without a
 * beat a live job reads `stale`; past `FLEET_JOB_RELEASE_AFTER_MS` the backend
 * watchdog releases it, freeing its key for any worker. A job that has never
 * beaten (`lastBeatAt: null`, a host on an older agent) is neither.
 */
export const FLEET_JOB_STALE_AFTER_MS = 5 * 60 * 1000;
export const FLEET_JOB_RELEASE_AFTER_MS = 15 * 60 * 1000;

/**
 * A live job whose round's own task log (the orchestrator's and the active step's, fleet-agent ADR 0019) has not
 * been written for this long reads `logSilent`: the host still beats, but the task it holds has gone quiet. The
 * engine heartbeats every 30s while a call is open, so a live task writes far more often. Flagged, never released —
 * a slow task is not killed (fleet-agent ADR 0012). A job whose holder never sent `logAt` is never log-silent.
 */
export const FLEET_JOB_LOG_SILENT_AFTER_MS = 10 * 60 * 1000;

/** The last line a command of a round reported, and when (fleet-agent ADR 0012). */
export const FleetJobProgressSchema = z.object({ at: z.iso.datetime(), line: z.string() });

export type FleetJobProgress = z.infer<typeof FleetJobProgressSchema>;

/**
 * A round a review judged stuck (fleet-agent ADR 0012). Flagged, never killed: the holder still beats, so the
 * watchdog leaves it alone, and an operator decides — clear the flag or release the job.
 */
export const FleetJobSuspectSchema = z.object({ since: z.iso.datetime(), reason: z.string() });

export type FleetJobSuspect = z.infer<typeof FleetJobSuspectSchema>;

export const LiveFleetJobSchema = z.object({
  jobId: z.uuid(),
  key: z.string(),
  worker: z.string(),
  machineName: z.string(),
  claimEpoch: z.number().int().positive(),
  claimedAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  /** Whether the CALLING machine is the holder — what the CLI marks its own rows with. */
  mine: z.boolean(),
  /** The holder's last heartbeat; `null` until a heartbeating host starts or beats it. */
  lastBeatAt: z.iso.datetime().nullable(),
  /** `lastBeatAt` older than `FLEET_JOB_STALE_AFTER_MS`. Never true while `lastBeatAt` is null. */
  stale: z.boolean(),
  /** The last line a command of the round reported, from the holder's beat (fleet-agent ADR 0012). */
  progress: FleetJobProgressSchema.nullable().optional(),
  /** A review judged the round stuck; kept until an operator clears it or the job ends (fleet-agent ADR 0012). */
  suspect: FleetJobSuspectSchema.nullable().optional(),
  /** The newest write to the round's own task log, from the holder's beat (fleet-agent ADR 0019); `null` until sent. */
  logAt: z.iso.datetime().nullable().optional(),
  /** `logAt` older than `FLEET_JOB_LOG_SILENT_AFTER_MS`. Never true while `logAt` is null. */
  logSilent: z.boolean().optional(),
});

export type LiveFleetJob = z.infer<typeof LiveFleetJobSchema>;

/**
 * An operator's "it's fine, leave it" on a suspect job (fleet-agent ADR 0012). The backend drops the flag and ignores
 * any suspect the holder still sends whose `since` is not after this clear.
 */
export const ClearFleetJobSuspectResponseSchema = z.object({ clearedAt: z.iso.datetime() });

export type ClearFleetJobSuspectResponse = z.infer<typeof ClearFleetJobSuspectResponseSchema>;

export const ListLiveFleetJobsResponseSchema = z.object({ data: z.array(LiveFleetJobSchema) });

export type ListLiveFleetJobsResponse = z.infer<typeof ListLiveFleetJobsResponseSchema>;

/**
 * A key still bound to the worker that last held it. The binding has no expiry:
 * only a manual release removes it, and until then any OTHER worker's claim on
 * the key is refused (`fleet.job.bound`).
 */
export const BoundFleetJobSchema = z.object({
  jobId: z.uuid(),
  key: z.string(),
  worker: z.string(),
  machineName: z.string(),
  claimEpoch: z.number().int().positive(),
  endReason: FleetJobEndReasonSchema,
  endedAt: z.iso.datetime(),
  boundAt: z.iso.datetime(),
  /** Whether the CALLING machine ran the round that ended it. */
  mine: z.boolean(),
});

export type BoundFleetJob = z.infer<typeof BoundFleetJobSchema>;

export const ListBoundFleetJobsResponseSchema = z.object({ data: z.array(BoundFleetJobSchema) });

export type ListBoundFleetJobsResponse = z.infer<typeof ListBoundFleetJobsResponseSchema>;

export const ReleaseHeldFleetJobBodySchema = z.object({ detail: z.string().max(500).optional() });

export type ReleaseHeldFleetJobBody = z.infer<typeof ReleaseHeldFleetJobBodySchema>;

export const ReleaseHeldFleetJobResponseSchema = z.object({ endedAt: z.iso.datetime() });

/**
 * Releasing by WORKER rather than by job id (IMM-938).
 *
 * The case this exists for is a worker that knows it is stuck and cannot name
 * the job holding it — its round died before recording the `jobId`, or it is
 * talking to a control plane too old to return one. It always knows its own
 * name, and a worker name already binds a machine to an agent
 * (`mini-pc-tester-1`).
 *
 * Unambiguous by construction: the partial unique index permits exactly ONE
 * live job per worker, which is the same rule that produces `worker-busy`.
 */
export const ReleaseFleetJobsByWorkerBodySchema = z.object({
  worker: z.string().min(1).max(128),
  detail: z.string().max(500).optional(),
  /**
   * A manual release: also drop every binding this worker holds, so any worker
   * may claim those keys. Omitted, the live job ends but stays bound to the
   * worker — what a stopping daemon or the intake's own stale-hold heal wants.
   */
  unbind: z.boolean().optional(),
});

export type ReleaseFleetJobsByWorkerBody = z.infer<typeof ReleaseFleetJobsByWorkerBodySchema>;

export const ReleaseFleetJobsByWorkerResponseSchema = z.object({
  /** The job that was holding this worker, or `null` when it was holding none. */
  released: z.object({ jobId: z.uuid(), key: z.string(), endedAt: z.iso.datetime() }).nullable(),
  /** The ended jobs whose binding this call dropped (`unbind` only). */
  unbound: z.array(z.object({ jobId: z.uuid(), key: z.string() })),
});

export type ReleaseFleetJobsByWorkerResponse = z.infer<typeof ReleaseFleetJobsByWorkerResponseSchema>;

/** The work items a machine wants counted — `fleet-agent agent preview` asks for every ticket it draws, once per lane. */
export const ListFleetJobHistoryBodySchema = z.object({ keys: z.array(z.string().min(1).max(255)).max(1000) });

export type ListFleetJobHistoryBody = z.infer<typeof ListFleetJobHistoryBodySchema>;

export const FleetJobKeyHistoryEntrySchema = FleetJobKeyHistorySchema.extend({
  key: z.string(),
  /** The key's effective run cap: `FLEET_JOB_MAX_RUNS` plus every grant (OPS-33). */
  limit: z.number().int().positive(),
});

export type FleetJobKeyHistoryEntry = z.infer<typeof FleetJobKeyHistoryEntrySchema>;

/** Only keys with at least one round come back; an absent key has never run. */
export const ListFleetJobHistoryResponseSchema = z.object({ data: z.array(FleetJobKeyHistoryEntrySchema) });

export type ListFleetJobHistoryResponse = z.infer<typeof ListFleetJobHistoryResponseSchema>;

/** An operator grants a work item extra runs past `FLEET_JOB_MAX_RUNS` (OPS-33). */
export const TopUpFleetJobBodySchema = z.object({
  key: z.string().min(1).max(255),
  runs: z.number().int().min(1).max(1000),
  reason: z.string().max(500).optional(),
});

export type TopUpFleetJobBody = z.infer<typeof TopUpFleetJobBodySchema>;

export const TopUpFleetJobResponseSchema = z.object({ key: z.string(), limit: z.number().int().positive() });

export type TopUpFleetJobResponse = z.infer<typeof TopUpFleetJobResponseSchema>;

export const ListResumableFleetJobsBodySchema = z.object({ worker: z.string().min(1).max(128) });

export type ListResumableFleetJobsBody = z.infer<typeof ListResumableFleetJobsBodySchema>;

/** A bound job whose every question is answered — its worker should resume it before taking anything else. */
export const ResumableFleetJobSchema = z.object({
  jobId: z.uuid(),
  key: z.string(),
  ticket: z.string(),
  boundAt: z.iso.datetime(),
  /** Answers not yet on the ticket; the resuming worker relays them before it claims. */
  answers: z.array(FleetAnswerToRelaySchema),
});

export type ResumableFleetJob = z.infer<typeof ResumableFleetJobSchema>;

export const ListResumableFleetJobsResponseSchema = z.object({ data: z.array(ResumableFleetJobSchema) });

export type ListResumableFleetJobsResponse = z.infer<typeof ListResumableFleetJobsResponseSchema>;

export type ReleaseHeldFleetJobResponse = z.infer<typeof ReleaseHeldFleetJobResponseSchema>;

/**
 * The operator's view of a held job (IMM-914.2). `silent` is computed on read
 * from the holding machine's `lastSeenAt` — never stored, because a stored one
 * is a cache of a clock and is wrong between the write and the read.
 */
export const FleetJobViewSchema = z.object({
  id: z.uuid(),
  key: z.string(),
  worker: z.string(),
  machineId: z.uuid(),
  machineName: z.string(),
  metadata: z.record(z.string(), z.unknown()),
  claimedAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  lastSeenAt: z.iso.datetime(),
  silent: z.boolean(),
  /** The holder's last heartbeat; `null` for a host that predates it. */
  lastBeatAt: z.iso.datetime().nullable(),
  /** `lastBeatAt` older than `FLEET_JOB_STALE_AFTER_MS`. Never true while `lastBeatAt` is null. */
  stale: z.boolean(),
  /** The round's last reported line (fleet-agent ADR 0012); `null` until the holder reports one. */
  progress: FleetJobProgressSchema.nullable(),
  /** Set while a review judges the round stuck (fleet-agent ADR 0012); `null` otherwise. */
  suspect: FleetJobSuspectSchema.nullable(),
  /** The newest write to the round's own task log (fleet-agent ADR 0019); `null` until the holder sends one. */
  logAt: z.iso.datetime().nullable(),
  /** `logAt` older than `FLEET_JOB_LOG_SILENT_AFTER_MS`. Never true while `logAt` is null. */
  logSilent: z.boolean(),
  /** Unanswered questions this live job has asked (IMM-917). */
  openQuestions: z.number().int().min(0),
  history: FleetJobKeyHistorySchema,
});

export type FleetJobView = z.infer<typeof FleetJobViewSchema>;

export const FLEET_JOB_STATES = ['all', 'silent', 'held'] as const;
export const FleetJobStateSchema = z.enum(FLEET_JOB_STATES);
export type FleetJobState = (typeof FLEET_JOB_STATES)[number];

export const ListFleetJobsQuerySchema = z.object({ state: FleetJobStateSchema.optional() });

export type ListFleetJobsQuery = z.infer<typeof ListFleetJobsQuerySchema>;

export const ListFleetJobsResponseSchema = z.object({ data: z.array(FleetJobViewSchema) });

export type ListFleetJobsResponse = z.infer<typeof ListFleetJobsResponseSchema>;

export const ReleaseFleetJobBodySchema = z.object({ detail: z.string().max(500).optional() });

export type ReleaseFleetJobBody = z.infer<typeof ReleaseFleetJobBodySchema>;

/**
 * A person's message to a live job (OPS-55): `stop` or an instruction, delivered to the holding host as a `steer`
 * command. Trimmed before the length check, so whitespace alone is refused.
 */
export const SendFleetJobMessageBodySchema = z
  .object({ text: z.string().trim().min(1).max(FLEET_JOB_MESSAGE_MAX_LENGTH) })
  .strict();

export type SendFleetJobMessageBody = z.infer<typeof SendFleetJobMessageBodySchema>;

/**
 * Where a job message stands, read off its `steer` command: `waiting` for the host to take it, `sent` (taken),
 * `received` (the host has it in hand), `delivered` (in the worker's steer inbox — the run reads it at its next
 * tool call), `not-delivered` (refused, failed or timed out; `detail` says why).
 */
export const FLEET_JOB_MESSAGE_DELIVERIES = ['waiting', 'sent', 'received', 'delivered', 'not-delivered'] as const;
export const FleetJobMessageDeliverySchema = z.enum(FLEET_JOB_MESSAGE_DELIVERIES);
export type FleetJobMessageDelivery = (typeof FLEET_JOB_MESSAGE_DELIVERIES)[number];

export const FleetJobMessageViewSchema = z.object({
  /** The `steer` command's id — the message IS that command. */
  id: z.uuid(),
  jobId: z.uuid(),
  text: z.string(),
  /** A bare `stop`: the run ends at its next safe point. */
  stop: z.boolean(),
  authorName: z.string(),
  sentAt: z.iso.datetime(),
  delivery: FleetJobMessageDeliverySchema,
  /** When it reached the worker's inbox; `null` until `delivered`. */
  deliveredAt: z.iso.datetime().nullable(),
  detail: z.string().nullable(),
});

export type FleetJobMessageView = z.infer<typeof FleetJobMessageViewSchema>;

export const ListFleetJobMessagesResponseSchema = z.object({ data: z.array(FleetJobMessageViewSchema) });

export type ListFleetJobMessagesResponse = z.infer<typeof ListFleetJobMessagesResponseSchema>;
